import React, { createContext, useCallback, useContext, useEffect, useRef, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { childrenProps } from "../types";

/**
 * 画面離脱ガード。
 * 未保存の変更がある画面が離脱確認を挟みたい場合に useNavigationGuard() でガードを登録する。
 * 登録がない画面は素通りするため、既存の遷移挙動は変わらない。
 *
 * proceed: 確認後に本来の遷移を実行するための関数
 * 戻り値: true = 遷移を止めて画面側で確認を出した / false = そのまま遷移してよい
 */
export type navigationGuard = (proceed: () => void) => boolean;

const NavigationGuardContext = createContext(
  {} as {
    setGuard: (guard: navigationGuard | null) => void;
    guardedNavigate: (to: string) => void;
  }
);

export const NavigationGuardProvider = (props: childrenProps) => {
  const navigate = useNavigate();
  const guardRef = useRef<navigationGuard | null>(null);

  const setGuard = useCallback((guard: navigationGuard | null) => {
    guardRef.current = guard;
  }, []);

  const guardedNavigate = useCallback(
    (to: string) => {
      const proceed = () => navigate(to);
      if (guardRef.current && guardRef.current(proceed)) {
        return;
      }
      proceed();
    },
    [navigate]
  );

  const data = { setGuard, guardedNavigate };
  return <NavigationGuardContext.Provider value={data} {...props} />;
};

export const useNavigationGuardContext = () => {
  return useContext(NavigationGuardContext);
};

/**
 * 画面側でガードを登録する。アンマウント時に解除する。
 * guard は毎レンダーの最新版が呼ばれるため、state を直接参照してよい。
 */
export const useNavigationGuard = (guard: navigationGuard) => {
  const { setGuard } = useNavigationGuardContext();
  const latestGuard = useRef(guard);
  latestGuard.current = guard;

  useEffect(() => {
    setGuard((proceed) => latestGuard.current(proceed));
    return () => setGuard(null);
  }, [setGuard]);
};

/**
 * ガードを経由して遷移するリンク。共通コンポーネントの <Link> の置き換え用。
 */
export const GuardedLink = (props: { to: string; className?: string; children: ReactNode }) => {
  const { guardedNavigate } = useNavigationGuardContext();
  return (
    <a
      href={props.to}
      className={props.className}
      onClick={(e) => {
        // 別タブで開く操作、およびProvider外での描画はブラウザ既定の挙動に任せる
        if (!guardedNavigate || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        guardedNavigate(props.to);
      }}
    >
      {props.children}
    </a>
  );
};
